import { useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import CustomModal from "../CustomModal";

const getFilePath = (file) => {
  if (!file) return "";
  if (typeof file === "string") return file;

  return (
    file?.file_path ||
    file?.fileName ||
    file?.file_name ||
    file?.path ||
    file?.url ||
    file?.image ||
    ""
  );
};

const buildAttachmentUrl = (filePath = "") => {
  const value = String(filePath || "").trim();
  if (!value) return "";
  if (/^(https?:|blob:|data:)/i.test(value)) return value;
  return `${process.env.REACT_APP_API_FILES_URL || ""}${value}`;
};

const getAttachmentName = (url = "") => {
  const cleanUrl = String(url || "").split("?")[0];
  const name = cleanUrl.split("/").pop() || "Attachment";

  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
};

const isImageAttachment = (url = "") =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(url).split("?")[0]);

const AttachmentViewerModal = ({
  show,
  files = [],
  heading = "Attachments",
  handleClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const urls = useMemo(
    () =>
      (Array.isArray(files) ? files : [])
        .map(getFilePath)
        .filter(Boolean)
        .map(buildAttachmentUrl)
        .filter(Boolean),
    [files]
  );

  useEffect(() => {
    if (show) setCurrentIndex(0);
  }, [show, urls.length]);

  const currentAttachmentUrl = urls[currentIndex] || "";
  const currentAttachmentName = getAttachmentName(currentAttachmentUrl);
  const hasMultipleAttachments = urls.length > 1;

  const changeAttachmentSlide = (direction) => {
    setCurrentIndex((previousIndex) => {
      const total = urls.length;
      if (!total) return previousIndex;
      return (previousIndex + direction + total) % total;
    });
  };

  return (
    <CustomModal
      show={show}
      handleClose={handleClose}
      heading={heading}
      size="lg"
      dialogClassName="attachment_viewer_modal"
    >
      <div className="attachment_viewer_modal_body">
        <div className="attachment_viewer_stage">
          {hasMultipleAttachments ? (
            <>
              <button
                type="button"
                className="attachment_viewer_nav attachment_viewer_nav_prev"
                onClick={() => changeAttachmentSlide(-1)}
                aria-label="Previous attachment"
              >
                <FiChevronLeft size={22} />
              </button>
              <button
                type="button"
                className="attachment_viewer_nav attachment_viewer_nav_next"
                onClick={() => changeAttachmentSlide(1)}
                aria-label="Next attachment"
              >
                <FiChevronRight size={22} />
              </button>
            </>
          ) : null}

          {urls.length ? (
            <>
              <span className="attachment_viewer_count">
                {currentIndex + 1} / {urls.length}
              </span>
              {isImageAttachment(currentAttachmentUrl) ? (
                <img
                  src={currentAttachmentUrl}
                  alt={currentAttachmentName}
                  className="attachment_viewer_image"
                />
              ) : (
                <iframe
                  src={currentAttachmentUrl}
                  title={currentAttachmentName}
                  className="attachment_viewer_frame"
                />
              )}
            </>
          ) : (
            <div className="attachment_viewer_empty">No attachments found</div>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default AttachmentViewerModal;
